import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { USER } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError } from "../helpers";
import { tryConfirmLogin } from "./authReducer";
import { IRentalItemAttachment } from "./rentalItemsAttachmentsReducer";

export interface IServiceResource {
    _id: string;
    name: string;
    content?: string;
    lectures?: IRentalItemAttachment[];
    imgSrc?: string;
    unit?: string;
    unitPrice: number;
    quantityLeft?: number;
    quantityPerRender?: number;
    quantity?: number;
}

class RegServiceResourceErrs {
    form: string | null = null;
    name: string | null = null;
    content: string | null = null;
    resourceImg: string | null = null;
    unit: string | null = null;
    unitPrice: string | null = null;
    quantityLeft: string | null = null;
}

export interface IServicesResourcesState {
    registeringServiceResource: boolean;
    registeredServiceResource: boolean;
    regServiceResourceErrs: RegServiceResourceErrs;
    fetchingServicesResources: boolean;
    servicesResources: IServiceResource[];
    mainServiceResource: IServiceResource | null;
    fetchServicesResourcesErr: string | null;
    updatingServiceResource: boolean;
    updatedServiceResource: boolean;
    updateServiceResourceErrs: RegServiceResourceErrs;
    deletingServiceResource: boolean;
    deletedServiceResource: boolean;
    deleteServiceResourceErr: string | null;
}

const initialState = {
    registeringServiceResource: false,
    registeredServiceResource: false,
    regServiceResourceErrs: new RegServiceResourceErrs(),
    fetchingServicesResources: false,
    servicesResources: [],
    mainServiceResource: null,
    fetchServicesResourcesErr: null,
    updatingServiceResource: false,
    updatedServiceResource: false,
    updateServiceResourceErrs: new RegServiceResourceErrs(),
    deletingServiceResource: false,
    deletedServiceResource: false,
    deleteServiceResourceErr: null

} as IServicesResourcesState;

const servicesResourcesSlice = createSlice({
    name: 'servicesResources',
    initialState,
    reducers: {
        clearRegServiceResourceFieldErr: (state: IServicesResourcesState, action: PayloadAction<string>) => {
            state.regServiceResourceErrs[action.payload as keyof RegServiceResourceErrs] = null;
        },
        clearRegServiceResourceErrs: (state: IServicesResourcesState) => {
            state.regServiceResourceErrs = new RegServiceResourceErrs();
        },
        tryResetRegServiceResourceFormErr: (state: IServicesResourcesState) => {
            let shouldReset = true;

            for (let key in state.regServiceResourceErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.regServiceResourceErrs[key as keyof RegServiceResourceErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.regServiceResourceErrs.form = null;
            }
        },
        resetRegisteredServiceResource: (state: IServicesResourcesState) => {
            state.registeredServiceResource = false;
        },
        regServiceResourceStart: (state: IServicesResourcesState) => {
            state.regServiceResourceErrs = new RegServiceResourceErrs();
            state.registeredServiceResource = false;
            state.registeringServiceResource = true;
        },
        regServiceResourcePass: (state: IServicesResourcesState) => {
            state.registeringServiceResource = false;
            state.registeredServiceResource = true;
        },
        regServiceResourceFail: (state: IServicesResourcesState, action: PayloadAction<IFieldError[]>) => {
            state.registeringServiceResource = false;


            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.regServiceResourceErrs) {
                    if (err.field === key) {
                        state.regServiceResourceErrs[key as keyof RegServiceResourceErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.regServiceResourceErrs.form) {
                state.regServiceResourceErrs.form = 'Form incorrectly filled.';
            }
        },
        setMainServiceResource: (state: IServicesResourcesState, action: PayloadAction<IServiceResource | null>) => {
            state.mainServiceResource = action.payload;
        },
        clearFetchServicesResourcesErr: (state: IServicesResourcesState) => {
            state.fetchServicesResourcesErr = null;
        },
        fetchServicesResourcesStart: (state: IServicesResourcesState) => {
            state.fetchServicesResourcesErr = null;
            state.fetchingServicesResources = true;
        },
        fetchServicesResourcesPass: (state: IServicesResourcesState, action: PayloadAction<IServiceResource[]>) => {
            state.fetchingServicesResources = false;
            state.servicesResources = action.payload;
        },
        fetchServicesResourcesFail: (state: IServicesResourcesState, action: PayloadAction<string>) => {
            state.fetchingServicesResources = false;
            state.fetchServicesResourcesErr = action.payload;
        },
        clearUpdateServiceResourceFieldErr: (state: IServicesResourcesState, action: PayloadAction<string>) => {
            state.updateServiceResourceErrs[action.payload as keyof RegServiceResourceErrs] = null;
        },
        clearUpdateServiceResourceErrs: (state: IServicesResourcesState) => {
            state.updateServiceResourceErrs = new RegServiceResourceErrs();
        },
        tryResetUpdateServiceResourceFormErr: (state: IServicesResourcesState) => {
            let shouldReset = true;

            for (let key in state.updateServiceResourceErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.updateServiceResourceErrs[key as keyof RegServiceResourceErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.updateServiceResourceErrs.form = null;
            }
        },
        resetUpdatedServiceResource: (state: IServicesResourcesState) => {
            state.updatedServiceResource = false;
        },
        updateServiceResourceStart: (state: IServicesResourcesState) => {
            state.updatedServiceResource = false;
            state.updateServiceResourceErrs = new RegServiceResourceErrs();
            state.updatingServiceResource = true;
        },
        updateServiceResourcePass: (state: IServicesResourcesState, action: PayloadAction<IServiceResource>) => {
            state.updatingServiceResource = false;
            state.updatedServiceResource = true;
        },
        updateServiceResourceFail: (state: IServicesResourcesState, action: PayloadAction<IFieldError[]>) => {
            state.updatingServiceResource = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.updateServiceResourceErrs) {
                    if (err.field === key) {
                        state.updateServiceResourceErrs[key as keyof RegServiceResourceErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.updateServiceResourceErrs.form) {
                state.updateServiceResourceErrs.form = 'Form incorrectly filled.';
            }
        },
        clearDeletServiceResourceErr: (state: IServicesResourcesState) => {
            state.deleteServiceResourceErr = null;
        },
        resetDeletedServiceResource: (state: IServicesResourcesState) => {
            state.deletedServiceResource = false;
        },
        deleteServiceResourceStart: (state: IServicesResourcesState) => {
            state.deleteServiceResourceErr = null;
            state.deletedServiceResource = false;
            state.deletingServiceResource = true;
        },
        deleteServiceResourcePass: (state: IServicesResourcesState) => {
            state.deletedServiceResource = true;
            state.deletingServiceResource = false;
        },
        deleteServiceResourceFail: (state: IServicesResourcesState, action: PayloadAction<string>) => {
            state.deleteServiceResourceErr = action.payload;
            state.deletingServiceResource = false;
        }
    }
});

export const {
    clearRegServiceResourceErrs,
    clearRegServiceResourceFieldErr,
    tryResetRegServiceResourceFormErr,
    resetRegisteredServiceResource,
    regServiceResourceStart,
    regServiceResourcePass,
    regServiceResourceFail,
    setMainServiceResource,
    clearFetchServicesResourcesErr,
    fetchServicesResourcesStart,
    fetchServicesResourcesPass,
    fetchServicesResourcesFail,
    clearUpdateServiceResourceErrs,
    clearUpdateServiceResourceFieldErr,
    tryResetUpdateServiceResourceFormErr,
    resetUpdatedServiceResource,
    updateServiceResourceStart,
    updateServiceResourcePass,
    updateServiceResourceFail,
    clearDeletServiceResourceErr,
    resetDeletedServiceResource,
    deleteServiceResourceStart,
    deleteServiceResourcePass,
    deleteServiceResourceFail
} = servicesResourcesSlice.actions;

interface ITryRegServiceResourceArg {
    formData: FormData;
    authToken: string;
}

export const tryRegServiceResource = createAsyncThunk('servicesResources/tryRegServiceResource', (arg: ITryRegServiceResourceArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/service-resource`;

    const { formData, authToken } = arg;

    dispatch(regServiceResourceStart());

    axios.post(endpoint, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(regServiceResourcePass());
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(regServiceResourceFail(fieldErrors));
    })
});

interface ServicesResourcesArg {
    authToken: string;
}

export const tryFetchServicesResources = createAsyncThunk('servicesResources/tryFetchServicesResources', (arg: ServicesResourcesArg, { dispatch }) => {
    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/services-resources`;

    const { authToken } = arg;

    dispatch(fetchServicesResourcesStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchServicesResourcesPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({authToken}));
            return;
        }
        let error = '';

        if (data?.err && typeof data?.err === typeof new String('')) {
            error = data.err;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(fetchServicesResourcesFail(error));
    });
});

interface ITryUpdateServiceResourceArg {
    serviceResourceId: string;
    data: FormData;
    authToken: string;
}

export const tryUpdateServiceResource = createAsyncThunk('servicesResources/tryUpdateServiceResource', (arg: ITryUpdateServiceResourceArg, { dispatch }) => {

    const { serviceResourceId, data, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/services-resources/${serviceResourceId}`;

    dispatch(updateServiceResourceStart());

    axios.put(endpoint, data, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        socket.emit(APP_UPDATE_AVAILABLE, USER);
        dispatch(updateServiceResourcePass(res.data));
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.err) {
            fieldErrors = [{ field: 'form', msg: data.err }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(updateServiceResourceFail(fieldErrors));
    })
});

interface IDeleteServiceResourceArg {
    authToken: string;
    serviceResourceId: string;
}

export const tryDeleteServiceResource = createAsyncThunk('serviceResources/deleteServiceResource', (arg: IDeleteServiceResourceArg, { dispatch }) => {
    const { authToken, serviceResourceId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/services-resources/${serviceResourceId}`;

    dispatch(deleteServiceResourceStart())
    axios.delete(endpoint, {
        headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
        }
    }).then(res => {
        dispatch(deleteServiceResourcePass());
        socket.emit(APP_UPDATE_AVAILABLE, USER);
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }
        let error = '';

        if (data?.err) {
            error = data.err;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(deleteServiceResourceFail(error));
    });
});


export default servicesResourcesSlice.reducer;